import { createAsyncThunk } from '@reduxjs/toolkit'
import { isCommonResponseSuccessful, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  selectEmrSectionsData,
  setEmrSectionsData,
  setEmrSectionsError,
  setEmrSectionsState,
} from '../prescriptionReducer'

const inFlightEmrSectionsRequests = new Map()
const emrSectionsCache = new Map()
const EMR_SECTIONS_CACHE_TTL_MS = 5 * 60 * 1000

/**
 * Detects the current portal based on the window pathname
 * @returns {string|null} Portal type or null if cannot be determined
 */
function detectCurrentPortal() {
  if (typeof window === 'undefined') return null
  const pathname = window.location.pathname
  if (pathname.startsWith('/support-home') || pathname.startsWith('/support/')) {
    return 'support'
  }
  if (pathname.startsWith('/mentor-home') || pathname.startsWith('/mentor/')) {
    return 'mentor'
  }
  return null
}

// Thunk
export const fetchEmrSections = createAsyncThunk(
  'prescriptionStore/fetchEmrSections',
  async ({ forceUpdateSections = false, practitionerId, practitionerSlug } = {}, { getState, dispatch }) => {
    const state = getState()
    const existingData = selectEmrSectionsData(state)?.data
    const currentPortal = detectCurrentPortal()
    const requestKey = `${currentPortal || 'mentor'}|${practitionerId || ''}|${practitionerSlug || ''}`

    if (existingData && Object.keys(existingData).length > 0) {
      if (!forceUpdateSections) {
        // Data already cached — skip dispatch entirely to avoid creating new Redux
        // object references that cause unnecessary re-renders across all subscribers
        emrSectionsCache.set(requestKey, {
          data: existingData,
          timestamp: Date.now(),
        })
        return existingData
      }
      // forceUpdateSections=true: proceed to fetch fresh data below
    } else {
      dispatch(setEmrSectionsState('loading'))
    }

    const cached = emrSectionsCache.get(requestKey)
    if (!forceUpdateSections && cached && Date.now() - cached.timestamp < EMR_SECTIONS_CACHE_TTL_MS) {
      dispatch(setEmrSectionsData(cached.data))
      dispatch(setEmrSectionsState('success'))
      return cached.data
    }

    if (!forceUpdateSections && inFlightEmrSectionsRequests.has(requestKey)) {
      return inFlightEmrSectionsRequests.get(requestKey)
    }

    try {
      let resp
      const requestPromise = (async () => {
        if (currentPortal === 'support') {
          // Support portal API - requires practitioner-id or practitioner-slug
          if (!practitionerId && !practitionerSlug) {
            throw new Error('practitionerId or practitionerSlug is required for support portal')
          }
          const params = {}
          if (practitionerSlug) {
            params['practitioner-slug'] = practitionerSlug
          } else if (practitionerId) {
            params['practitioner-id'] = practitionerId
          }
          resp = await getSPDataWithParams(
            '/api/secure/cc/support/practitioner/emr-sections',
            params,
            undefined,
            undefined,
            true,
          )
          if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
            const data = resp?.data?.data || resp?.data
            const emrSections = data?.emrSections || data
            dispatch(setEmrSectionsData(emrSections))
            dispatch(setEmrSectionsState('success'))
            emrSectionsCache.set(requestKey, {
              data: emrSections,
              timestamp: Date.now(),
            })
            return emrSections
          }
          throw new Error('Invalid response from API')
        }

        // Mentor portal API (default)
        resp = await getMPDataWithoutParams(
          '/api/secure/practitioner/clinical/emr-settings/emr-sections',
        )
        if (resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)) {
          const sectionsData = resp?.data?.data || {}
          dispatch(setEmrSectionsData(sectionsData))
          dispatch(setEmrSectionsState('success'))
          emrSectionsCache.set(requestKey, {
            data: sectionsData,
            timestamp: Date.now(),
          })
          return sectionsData
        }
        throw new Error('Invalid response from API')
      })()

      inFlightEmrSectionsRequests.set(requestKey, requestPromise)
      return await requestPromise
    } catch (error) {
      dispatch(setEmrSectionsError(error.message || 'Failed to fetch EMR sections'))
      dispatch(setEmrSectionsState('error'))
    } finally {
      inFlightEmrSectionsRequests.delete(requestKey)
    }
  },
)
